import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { TRANSLATIONS } from "@/i18n/translations";

const LocaleContext = createContext(null);

const LOCALE_KEY = "levami_locale";
const CURRENCY_KEY = "levami_currency";

export const LOCALE_LABELS = { fr: "Français", en: "English", de: "Deutsch" };
export const CURRENCY_LABELS = { eur: "EUR (€)", gbp: "GBP (£)" };

/** Detects the visitor's preferred language from their browser settings.
 * Rule: French browser -> fr, German browser -> de, anything else -> en (default fallback). */
function detectBrowserLocale() {
  try {
    const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || navigator.userLanguage || ""];
    for (const raw of langs) {
      const lang = String(raw || "").slice(0, 2).toLowerCase();
      if (lang === "fr") return "fr";
      if (lang === "de") return "de";
    }
  } catch {
    /* noop */
  }
  return "en";
}

function initialLocale() {
  const stored = localStorage.getItem(LOCALE_KEY);
  if (stored) return stored;
  // First visit, no explicit choice yet: silently auto-apply the detected locale
  // (FR/DE if the browser is set to one of those, EN otherwise) and remember it.
  const detected = detectBrowserLocale();
  localStorage.setItem(LOCALE_KEY, detected);
  return detected;
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(initialLocale);
  const [currency, setCurrencyState] = useState(() => (localStorage.getItem(CURRENCY_KEY) || "eur").toLowerCase());

  const setLocale = useCallback((next) => {
    setLocaleState(next);
    localStorage.setItem(LOCALE_KEY, next);
  }, []);

  const setCurrency = useCallback((next) => {
    const val = (next || "eur").toLowerCase();
    setCurrencyState(val);
    localStorage.setItem(CURRENCY_KEY, val);
  }, []);

  const t = useCallback(
    (key, vars) => {
      let str = TRANSLATIONS[locale]?.[key] ?? TRANSLATIONS.fr[key] ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          str = str.replace(new RegExp(`{{${k}}}`, "g"), v);
        });
      }
      return str;
    },
    [locale]
  );

  const value = useMemo(
    () => ({ locale, setLocale, currency, setCurrency, t }),
    [locale, currency, t, setLocale, setCurrency]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
